import { create } from 'zustand'
import { useTabsStore } from '@/stores/tabsStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { usePlanDialogStore } from '@/stores/planDialogStore'

interface ScreenPosition {
  x: number
  y: number
}

export type ProjectImportSource =
  | 'editor-import'
  | 'new-project-menu'
  | 'project-list'
  | 'empty-state'

interface UseModalStoreState {
  newProjectOpen: boolean
  defaultsOpen: boolean
  groupsOpen: boolean
  exportOpen: boolean
  versionsOpen: boolean
  shortcutsOpen: boolean
  canvasGuideOpen: boolean
  aboutOpen: boolean
  relationPopup: {
    open: boolean
    relationId: string | null
    position: ScreenPosition | null
  }
  fkPanel: {
    open: boolean
    tableId: string | null
    position: ScreenPosition | null
  }
  indexPanel: {
    open: boolean
    tableId: string | null
    position: ScreenPosition | null
  }
  sqlImport: {
    open: boolean
    source: ProjectImportSource
  }

  openNewProject: () => void
  closeNewProject: () => void
  openDefaults: () => void
  closeDefaults: () => void
  openGroups: () => void
  closeGroups: () => void
  openExport: () => void
  closeExport: () => void
  openVersions: () => void
  closeVersions: () => void
  openShortcuts: () => void
  closeShortcuts: () => void
  openCanvasGuide: () => void
  closeCanvasGuide: () => void
  openAbout: () => void
  closeAbout: () => void
  openRelationPopup: (relationId: string, position?: ScreenPosition | null) => void
  closeRelationPopup: () => void
  openFkPanel: (tableId: string, position?: ScreenPosition | null) => void
  closeFkPanel: () => void
  openIndexPanel: (tableId: string, position?: ScreenPosition | null) => void
  closeIndexPanel: () => void
  openSqlImport: (source?: ProjectImportSource) => void
  closeSqlImport: () => void
  createCommitOpen: boolean
  openCreateCommit: () => void
  closeCreateCommit: () => void
  conflict: { open: boolean; currentCommitId: number }
  openConflict: (currentCommitId: number) => void
  closeConflict: () => void
  openProjectPickerOpen: boolean
  openOpenProjectPicker: () => void
  closeOpenProjectPicker: () => void
}

export const useModalStore = create<UseModalStoreState>()((set) => ({
  newProjectOpen: false,
  defaultsOpen: false,
  groupsOpen: false,
  exportOpen: false,
  versionsOpen: false,
  shortcutsOpen: false,
  canvasGuideOpen: false,
  aboutOpen: false,
  relationPopup: {
    open: false,
    relationId: null,
    position: null,
  },
  fkPanel: {
    open: false,
    tableId: null,
    position: null,
  },
  indexPanel: {
    open: false,
    tableId: null,
    position: null,
  },
  sqlImport: {
    open: false,
    source: 'editor-import',
  },
  createCommitOpen: false,
  conflict: { open: false, currentCommitId: 0 },
  openProjectPickerOpen: false,

  openNewProject: () => set({ newProjectOpen: true }),
  closeNewProject: () => set({ newProjectOpen: false }),
  openDefaults: () => set({ defaultsOpen: true }),
  closeDefaults: () => set({ defaultsOpen: false }),
  openGroups: () => set({ groupsOpen: true }),
  closeGroups: () => set({ groupsOpen: false }),
  openExport: () => {
    if (!useTabsStore.getState().activeTabId) return
    if (isFreeCurrentWorkspace()) {
      usePlanDialogStore.getState().openDialog()
      return
    }
    set({ exportOpen: true })
  },
  closeExport: () => set({ exportOpen: false }),
  openVersions: () => {
    if (!useTabsStore.getState().activeTabId) return
    if (isFreeCurrentWorkspace()) {
      usePlanDialogStore.getState().openDialog()
      return
    }
    set({ versionsOpen: true })
  },
  closeVersions: () => set({ versionsOpen: false }),
  openShortcuts: () => set({ shortcutsOpen: true }),
  closeShortcuts: () => set({ shortcutsOpen: false }),
  openCanvasGuide: () => set({ canvasGuideOpen: true }),
  closeCanvasGuide: () => set({ canvasGuideOpen: false }),
  openAbout: () => set({ aboutOpen: true }),
  closeAbout: () => set({ aboutOpen: false }),

  openRelationPopup: (relationId, position = null) =>
    set({
      relationPopup: {
        open: true,
        relationId,
        position,
      },
    }),
  closeRelationPopup: () =>
    set({
      relationPopup: {
        open: false,
        relationId: null,
        position: null,
      },
    }),

  openFkPanel: (tableId, position = null) =>
    set({
      fkPanel: {
        open: true,
        tableId,
        position,
      },
    }),
  closeFkPanel: () =>
    set({
      fkPanel: {
        open: false,
        tableId: null,
        position: null,
      },
    }),

  openIndexPanel: (tableId, position = null) =>
    set({
      indexPanel: {
        open: true,
        tableId,
        position,
      },
    }),
  closeIndexPanel: () =>
    set({
      indexPanel: {
        open: false,
        tableId: null,
        position: null,
      },
    }),

  openSqlImport: (source = 'editor-import') => {
    if (isFreeCurrentWorkspace()) {
      usePlanDialogStore.getState().openDialog()
      return
    }
    set({
      sqlImport: {
        open: true,
        source,
      },
    })
  },
  closeSqlImport: () =>
    set({
      sqlImport: {
        open: false,
        source: 'editor-import',
      },
    }),

  openCreateCommit: () => set({ createCommitOpen: true }),
  closeCreateCommit: () => set({ createCommitOpen: false }),

  openConflict: (currentCommitId) => set({ conflict: { open: true, currentCommitId } }),
  closeConflict: () => set({ conflict: { open: false, currentCommitId: 0 } }),

  openOpenProjectPicker: () => set({ openProjectPickerOpen: true }),
  closeOpenProjectPicker: () => set({ openProjectPickerOpen: false }),
}))

function isFreeCurrentWorkspace() {
  const tabsState = useTabsStore.getState()
  const activeTab = tabsState.tabs.find((tab) => tab.id === tabsState.activeTabId)
  const workspaceStore = useWorkspaceStore.getState()
  const targetWorkspaceId = activeTab?.workspaceId ?? workspaceStore.currentWorkspace?.id ?? null
  const workspace = workspaceStore.workspaces.find((item) => item.id === targetWorkspaceId)
    ?? workspaceStore.currentWorkspace
  const subscription = workspaceStore.currentWorkspace?.id === targetWorkspaceId
    ? workspaceStore.subscription
    : null
  const planCode =
    subscription?.plan.code ??
    workspace?.subscription_summary?.plan_code ??
    null

  return String(planCode ?? '').toLowerCase() === 'free'
}
